/**
 * ShareEvaluationModal — create and manage share links for an evaluation result.
 *
 * Renders inside the HeadlessUI v2 Modal (Dialog / DialogBackdrop / DialogPanel).
 * The caller passes `evaluationId` and controls open/close state.
 *
 * UX flow:
 *  1. Coach selects expiry (7 / 30 / 90 days). The shared page shows scores +
 *     coaching only (public video playback is not supported).
 *  2. Clicks "Create link" → calls POST /api/evaluations/{id}/share.
 *  3. On success: shows the resulting URL with a copy-to-clipboard button.
 *  4. Below: lists existing shares (status + view count) with Revoke action.
 */
import { useState, useEffect, useCallback } from 'react';
import Modal from '@/components/ui/Modal';
import Button from '@/components/Button';
import { Spinner } from '@/components/ui';
import Badge from '@/components/ui/Badge';
import { CheckIcon, ClipboardIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { LinkIcon, EyeIcon } from '@heroicons/react/24/solid';
import {
  createShare,
  listShares,
  revokeShare,
} from '@/services/shareApi';
import type { EvaluationShare, CreateShareResponse } from '@/services/shareApi';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatExpiry(isoDate: string): string {
  try {
    return new Date(isoDate).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

function statusBadgeVariant(status: EvaluationShare['status']): 'success' | 'error' | 'secondary' {
  if (status === 'active') return 'success';
  if (status === 'expired') return 'secondary';
  return 'error'; // revoked
}

// ─── Sub-component: created link row ─────────────────────────────────────────

interface CreatedLinkRowProps {
  shareUrl: string;
}

function CreatedLinkRow({ shareUrl }: CreatedLinkRowProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers that block clipboard without user gesture.
      const el = document.createElement('textarea');
      el.value = shareUrl;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.focus();
      el.select();
      try { document.execCommand('copy'); } catch { /* noop */ }
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [shareUrl]);

  return (
    <div className="rounded-xl border border-success/30 bg-success/5 p-4 space-y-3">
      <p className="text-sm font-semibold text-success">Link created</p>
      <div className="flex items-center gap-2">
        <input
          readOnly
          value={shareUrl}
          aria-label="Share link URL"
          className="flex-1 min-w-0 rounded-lg border border-border-subtle bg-dark-elevated-2 px-3 py-2 text-sm text-text-secondary font-mono truncate focus:outline-none focus:ring-1 focus:ring-accent/50"
        />
        <button
          type="button"
          onClick={handleCopy}
          aria-label={copied ? 'Copied' : 'Copy link'}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border border-border-subtle bg-dark-elevated-2 hover:bg-dark-card text-text-primary transition-colors shrink-0"
        >
          {copied ? (
            <>
              <CheckIcon className="h-4 w-4 text-success" />
              <span className="text-success">Copied</span>
            </>
          ) : (
            <>
              <ClipboardIcon className="h-4 w-4" />
              Copy
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Sub-component: existing share row ───────────────────────────────────────

interface ShareRowProps {
  share: EvaluationShare;
  onRevoke: (token: string) => void;
  isRevoking: boolean;
}

function ShareRow({ share, onRevoke, isRevoking }: ShareRowProps) {
  return (
    <div className="flex items-center justify-between gap-3 py-2.5 border-b border-border-subtle last:border-0">
      <div className="min-w-0 flex-1 space-y-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant={statusBadgeVariant(share.status)} size="sm">
            {share.status.charAt(0).toUpperCase() + share.status.slice(1)}
          </Badge>
          {share.status === 'active' && (
            <span className="text-xs text-text-muted">Expires {formatExpiry(share.expires_at)}</span>
          )}
          {share.status !== 'active' && share.revoked_at && (
            <span className="text-xs text-text-muted">Revoked {formatExpiry(share.revoked_at)}</span>
          )}
        </div>
        <div className="flex items-center gap-1 text-xs text-text-muted">
          <EyeIcon className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
          {share.view_count} view{share.view_count !== 1 ? 's' : ''}
        </div>
      </div>
      {share.status === 'active' && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onRevoke(share.token)}
          disabled={isRevoking}
          aria-label="Revoke this share link"
          className="shrink-0"
        >
          {isRevoking ? <Spinner className="h-3.5 w-3.5" /> : (
            <>
              <XMarkIcon className="h-3.5 w-3.5 mr-1" aria-hidden="true" />
              Revoke
            </>
          )}
        </Button>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const EXPIRY_OPTIONS = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

export interface ShareEvaluationModalProps {
  isOpen: boolean;
  onClose: () => void;
  evaluationId: string;
}

export default function ShareEvaluationModal({
  isOpen,
  onClose,
  evaluationId,
}: ShareEvaluationModalProps) {
  const [expiryDays, setExpiryDays] = useState(30);
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdLink, setCreatedLink] = useState<CreateShareResponse | null>(null);

  const [shares, setShares] = useState<EvaluationShare[]>([]);
  const [isLoadingShares, setIsLoadingShares] = useState(false);
  const [revokingToken, setRevokingToken] = useState<string | null>(null);

  // Fetch existing shares whenever the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setIsLoadingShares(true);
    listShares(evaluationId)
      .then(setShares)
      .catch(() => setShares([]))
      .finally(() => setIsLoadingShares(false));
  }, [isOpen, evaluationId]);

  const handleCreate = useCallback(async () => {
    setIsCreating(true);
    setCreateError(null);
    setCreatedLink(null);
    try {
      const result = await createShare(evaluationId, {
        expires_in_days: expiryDays,
        allow_video: false,
      });
      setCreatedLink(result);
      // Refresh the list so the new link appears immediately.
      const updated = await listShares(evaluationId);
      setShares(updated);
    } catch {
      setCreateError('Failed to create share link. Please try again.');
    } finally {
      setIsCreating(false);
    }
  }, [evaluationId, expiryDays]);

  const handleRevoke = useCallback(async (token: string) => {
    setRevokingToken(token);
    try {
      await revokeShare(token);
      // Update the share in-place to status=revoked to avoid an extra round-trip.
      setShares((prev) =>
        prev.map((s) =>
          s.token === token
            ? { ...s, status: 'revoked' as const, revoked_at: new Date().toISOString() }
            : s
        )
      );
    } catch {
      // Re-fetch to get accurate state if revoke fails.
      listShares(evaluationId).then(setShares).catch(() => null);
    } finally {
      setRevokingToken(null);
    }
  }, [evaluationId]);

  // Reset created link state when modal closes.
  const handleClose = useCallback(() => {
    setCreatedLink(null);
    setCreateError(null);
    onClose();
  }, [onClose]);

  const activeShares = shares.filter((s) => s.status === 'active');
  const pastShares = shares.filter((s) => s.status !== 'active');

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Share evaluation"
      size="md"
    >
      <div className="space-y-5">
        {/* ── Create form ── */}
        <div className="space-y-4">
          {/* Expiry select */}
          <div className="space-y-1.5">
            <label htmlFor="share-expiry" className="block text-sm font-medium text-text-primary">
              Link expires in
            </label>
            <select
              id="share-expiry"
              value={expiryDays}
              onChange={(e) => setExpiryDays(Number(e.target.value))}
              className="w-full rounded-lg border border-border-subtle bg-dark-elevated-2 px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/50"
            >
              {EXPIRY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Shared page shows scores + coaching only — public video playback
              is not supported (videos are served via the authenticated route). */}
          <p className="text-xs text-text-muted">
            The shared page shows scores and coaching feedback only — no video.
          </p>

          {createError && (
            <p role="alert" className="text-sm text-danger">
              {createError}
            </p>
          )}

          <Button
            onClick={handleCreate}
            disabled={isCreating}
            className="w-full"
            data-testid="share-create-button"
          >
            {isCreating ? (
              <span className="flex items-center gap-2">
                <Spinner className="h-4 w-4" />
                Creating...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <LinkIcon className="h-4 w-4" />
                Create link
              </span>
            )}
          </Button>
        </div>

        {/* ── Newly created link ── */}
        {createdLink && <CreatedLinkRow shareUrl={createdLink.share_url} />}

        {/* ── Existing shares ── */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-text-primary">Existing links</p>
            {isLoadingShares && <Spinner className="h-4 w-4 text-text-muted" />}
          </div>

          {!isLoadingShares && shares.length === 0 && (
            <p className="text-sm text-text-muted py-2">
              No share links yet. Create one above.
            </p>
          )}

          {/* Active shares first */}
          {activeShares.length > 0 && (
            <div>
              {activeShares.map((share) => (
                <ShareRow
                  key={share.token}
                  share={share}
                  onRevoke={handleRevoke}
                  isRevoking={revokingToken === share.token}
                />
              ))}
            </div>
          )}

          {/* Past (expired/revoked) shares — collapsed by default if many */}
          {pastShares.length > 0 && (
            <div className="opacity-60">
              <p className="text-xs font-semibold uppercase tracking-wider text-text-muted mb-1">
                Expired / Revoked
              </p>
              {pastShares.slice(0, 5).map((share) => (
                <ShareRow
                  key={share.token}
                  share={share}
                  onRevoke={handleRevoke}
                  isRevoking={revokingToken === share.token}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
